import React, { FC, Key, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Tree } from 'antd';
import { AntTreeNodeProps } from 'antd/es/tree';
import { SearchInput } from 'common/components';
import { DEFAULT_VALUE_0 } from 'common/config';
import {
  filterTreeDataBySearch,
  findTreeChildrenKeysByCheckedKey,
  findTreeParentKeysByCheckedKeys,
  getItemKeyFromTreeKey,
  getTreeNodeParentKey,
  highlightTreeTitle,
  transformIdToTreeKey,
} from 'common/helpers/tree.helper';
import { ITreeDataNode } from 'common/models';
import { ReactComponent as ArrowDownShortIcon } from 'app/assets/images/redesign/arrow-down-short.svg';
import { ReactComponent as ArrowRightShortIcon } from 'app/assets/images/redesign/arrow-right-short.svg';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  defaultValue?: number[];
  treeData: ITreeDataNode[];
  allTreeKeys: Key[];
  onClose: () => void;
  onSave: (value: number[] | null, onSuccess: () => void) => void;
}

export const ContractCategoriesModal: FC<IComponentProps> = (props) => {
  const { open, loading, defaultValue, treeData, allTreeKeys, onClose, onSave } = props;

  const [search, setSearch] = useState<string>('');
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const highlightedTreeData = useMemo(() => highlightTreeTitle(treeData, search), [treeData, search]);
  const checkAll = allTreeKeys.length > DEFAULT_VALUE_0 && checkedKeys.length === allTreeKeys.length;
  const indeterminate = checkedKeys.length > DEFAULT_VALUE_0 && checkedKeys.length < allTreeKeys.length;

  const onSearchChange = async (value = '') => {
    const matchedKeys = filterTreeDataBySearch(treeData, value);
    const newExpandedKeys = matchedKeys
      .map((key) => getTreeNodeParentKey(key, treeData))
      .filter((key, index, array): key is Key => key !== null && array.indexOf(key) === index);

    setSearch(value);
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(true);
  };

  const onCheckAllChange = (checked: boolean) => {
    checked ? setCheckedKeys(allTreeKeys) : setCheckedKeys([]);
  };

  const onCheck = (checked: boolean, node: ITreeDataNode) => {
    const parentKeys = findTreeParentKeysByCheckedKeys([node.key]);
    const childrenKeys = findTreeChildrenKeysByCheckedKey(treeData, node.key);
    const allKeys = [...parentKeys, node.key, ...childrenKeys];
    const keysWithChildren = [node.key, ...childrenKeys];

    if (checked) {
      const newCheckedKeys = [...checkedKeys, ...allKeys];

      setCheckedKeys([...new Set(newCheckedKeys)]);
    } else {
      const newCheckedKeys = checkedKeys.filter((key) => !keysWithChildren.includes(key));

      setCheckedKeys([...new Set(newCheckedKeys)]);
    }
  };

  const onExpand = (keys: Key[]) => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setSearch('');
    onClose();
  };

  const handleSave = () => {
    if (checkAll) {
      onSave(null, handleClose);
    } else {
      const ids = checkedKeys.map((key) => Number(getItemKeyFromTreeKey(key)));

      onSave(ids, handleClose);
    }
  };

  useEffect(() => {
    if (defaultValue) {
      const checkedKeys = defaultValue.map((value) => transformIdToTreeKey(value, allTreeKeys));
      const expandedKeys = findTreeParentKeysByCheckedKeys(checkedKeys);

      setCheckedKeys(checkedKeys);
      setExpandedKeys(expandedKeys);
    } else {
      setCheckedKeys(allTreeKeys);
      setExpandedKeys(allTreeKeys);
    }
  }, [defaultValue, allTreeKeys]);

  return (
    <Modal
      rootClassName="redesign modal contract-categories-modal"
      width={800}
      centered
      destroyOnClose
      closeIcon={false}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleClose} disabled={loading}>
          Отмена
        </Button>,
        <Button key="save" className="button-sm primary" onClick={handleSave} loading={loading}>
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-6">
        <SearchInput auto placeholder="Введите название бренда" minLength={DEFAULT_VALUE_0} onSearch={onSearchChange} />
      </div>

      <div className="mb-4 contract-categories-modal__check-all">
        <Checkbox
          className="mb-4"
          checked={checkAll}
          indeterminate={indeterminate}
          onChange={(e) => onCheckAllChange(e.target.checked)}
        >
          Все категории
        </Checkbox>
      </div>

      <div className="mb-6 contract-categories-modal__scroll-container">
        {highlightedTreeData.length ? (
          <Tree
            height={800}
            virtual
            defaultExpandAll
            treeData={highlightedTreeData}
            showLine
            switcherIcon={(props: AntTreeNodeProps) => {
              return props.expanded ? (
                <ArrowDownShortIcon className="icon-arrow-down-short-dark-grey" />
              ) : (
                <ArrowRightShortIcon className="icon-arrow-right-short-dark-grey" />
              );
            }}
            checkable
            checkStrictly
            checkedKeys={checkedKeys}
            onCheck={(_, { checked, node }) => onCheck(checked, node)}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            onExpand={onExpand}
          />
        ) : (
          <div style={{ margin: '20px 0', textAlign: 'center' }} className="text-body color-dark-grey">
            Список категорий пуст.
          </div>
        )}
      </div>
    </Modal>
  );
};
